import { CaretDownOutlined } from "@ant-design/icons";
import { Box, CircularProgress, FormControl, TextField } from "@mui/material";
import { Select as AntSelect, Space } from "antd";
import { Fragment, useEffect, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import { green, neutral, success } from "../../constants/colorConstants";
import { DIAL_CODE, EMPLOYEE_DATA_TYPE } from "../../constants/enums";
import { errorMessage } from "../../constants/messageConstants";
import {
  EMAIL_REGEX,
  PHONE_NUMBER_INDIA_REGEX,
  PHONE_NUMBER_SINGAPORE_REGEX,
} from "../../constants/regexConstants";
import { checkPermissions, fileTypeMatch, toastMessage } from "../../helper";
import { bulkUploadService } from "../../services/emissionService";
import {
  addEmployeeService,
  createDepartmentService,
  getDepartmentsService,
  updateEmployeeService,
} from "../../services/employeeService";
import { fetchUser } from "../../services/orgService";
import MapsInput from "../common/MapsInput";
import "./EmployeeCreation.scss";
import "./EmployeeList.scss";

function filter(inputValue: string, path: any) {
  return path.some(
    (option: any) =>
      option.label.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
  );
}

const styles = (theme: any) => ({
  ".MuiOutlinedInput-notchedOutline": {
    border: "1px solid #D7DFE9",
    borderRadius: "8px",
    height: "3rem",
  },
  "&:hover": {
    ".MuiOutlinedInput-notchedOutline": {
      border: "2px solid #59CE90 !important",
    },
  },
});

const customStyles = {
  dropdownIndicator: (base: any) => ({
    ...base,
    color: neutral[500],
    padding: "6px !important",
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    display: "none",
  }),
  control: (base: any, state: any) => ({
    ...base,
    boxShadow: "none",
    borderColor: "none",
    borderRadius: "8px",
    border: "1px solid #D7DFE9",
    height: "2.5rem",
    "&:hover": {
      border: "2px solid #59CE90 !important",
    },
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: "none",
    color: neutral[900],
    "&:hover": {
      backgroundColor: state.isFocused ? green[100] : "",
    },
  }),
  menu: (base: any, state: any) => ({
    ...base,
    boxShadow:
      "0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)",
    overflow: "hidden ",
    marginTop: "4px",
    borderRadius: "8px",
  }),
};
const EmployeeCreation = (props: any) => {
  const { state } = useLocation();
  const companyPreferences = useSelector(
    (state: any) => state.org.companyPreferences
  );
  const {
    setStep,
    countryOptions,
    industries,
    setIndustries,
    searchText,
    formData,
    setFormData,
    bunitsList,
    dispatch,
  } = props;
  const corporateEmployeeRoles = ["Manager", "Business User", "Verifier"];
  const trainerEmployeeRoles = [
    "Manager",
    "Business User",
    "Verifier",
    "Student",
  ];
  const [errorData, setErrorData] = useState({
    emailError: "",
    mobileError: "",
    registrationIdError: "",
    unitNameError: "",
  });
  const permissions = useSelector((state: any) => state.auth.userPermissions);
  const navigate = useNavigate();
  const [systemRoleTypes, setSystemRoleTypes] = useState(
    corporateEmployeeRoles
  );
  const {
    incharge,
    name,
    email,
    type,
    registrationId,
    address,
    country,
    timeZone,
    parentIndustryName,
    childIndustryName,
  } = formData;
  const { emailError, registrationIdError, mobileError, unitNameError } =
    errorData;
  const [industryTypeValue, setIndustryTypeValue] = useState([
    "hhh",
    "abcd",
  ] as any[]);
  const [incharges, setIncharges] = useState([] as any[]);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [controller, setController] = useState({
    page: 0,
    limit: 10,
  });
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [additionalTimeZones, setAdditionalTimeZones] = useState({
    "America/Adak": "America",
  });
  const [industryId, setIndustryId] = useState("");
  const [initialBunit, setInitialBunit] = useState([] as any[]);
  const [departmentOptions, setDepartmentOptions] = useState([] as any[]);
  const [isLoading, setIsLoading] = useState(false);

  const handleDeptChangeSelect = (newValue: any, actionMeta: any) => {
    setFormData({
      ...formData,
      department: newValue,
    });
  };

  const clearDeptInput = () => {
    setFormData({
      ...formData,
      department: null,
    });
  };

  const handleDeptCreateOption = (inputValue: any) => {
    const newOption = {
      label: inputValue,
      value: inputValue.trim(),
    };
    createDepartmentList(inputValue);
    setDepartmentOptions([...departmentOptions, newOption]);
    setFormData({
      ...formData,
      department: newOption,
    });
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const { Option } = AntSelect;
  const isEdit = state?.userId;
  let phone = "";
  if (isEdit) {
    phone = formData?.phone
      ? formData?.phone[0] !== "+"
        ? formData?.dialCode + formData?.phone
        : formData?.phone
      : "";
  }
  if (isEdit && !formData?.businessUnitIds) {
    formData.businessUnitIds = initialBunit;
  }
  if (parentIndustryName && childIndustryName && !industryTypeValue.length) {
    setIndustryTypeValue([parentIndustryName, childIndustryName]);
  }

  const handleCancel = () => {
    dispatch({ type: "clearDetailview" });
    navigate("/organisation/employee");
  };
  const onChange = (e: any, target: string) => {
    if (
      target === EMPLOYEE_DATA_TYPE.NAME ||
      target === EMPLOYEE_DATA_TYPE.ADDRESS ||
      target === EMPLOYEE_DATA_TYPE.DESIGNATION ||
      target === EMPLOYEE_DATA_TYPE.EMAIL ||
      target === EMPLOYEE_DATA_TYPE.EMPLOYEECODE
    ) {
      if (target === EMPLOYEE_DATA_TYPE.EMAIL) {
        validateFields("email", e?.target?.value);
      }
      setFormData({ ...formData, [e.target.name]: e.target.value });
    } else if (target === EMPLOYEE_DATA_TYPE.ROLENAME) {
      setFormData({ ...formData, roleName: e });
    } else if (target === EMPLOYEE_DATA_TYPE.DEPARTMENT) {
      setFormData({ ...formData, department: e });
    } else if (target === EMPLOYEE_DATA_TYPE.BUSINESSUNIT_IDs) {
      let newIds: any = e?.map((each: any) => {
        let data: any = bunitsList?.filter((curr: any) => curr.value === each);
        return data[0]?.value;
      });
      setFormData({ ...formData, businessUnitIds: [...newIds] });
    }
  };

  const onChangePhone = (e: string) => {
    if (e) {
      let phones: any = e.split(" ");
      let dialCode = phones[0];
      let mobile = "";
      phones.forEach((each: any, index: number) => {
        if (index > 0) {
          mobile = mobile + each + " ";
        }
      });
      setFormData({
        ...formData,
        phone: e,
        mobile: mobile,
        dialCode: dialCode,
      });
    }
    validateFields("phone", e);
  };
  const createEmployee = () => {
    setIsLoading(true);
    let resData = {
      ...formData,
      department: formData.department.value,
      companyId:
        localStorage.getItem("company") &&
        JSON.parse(localStorage.getItem("company") || "")?.id,
      companyName:
        localStorage.getItem("company") &&
        JSON.parse(localStorage.getItem("company") || "")?.name,
      type: localStorage.getItem("accountType") || "",
    };

    if (isEdit) {
      let removedUnitIds: any = [];
      let addedUnitIds: any = [];
      initialBunit.map((eachInitial: any) => {
        const matchedUnitIdsIndex = formData?.businessUnitIds.findIndex(
          (each: any) => each === eachInitial
        );
        if (matchedUnitIdsIndex === -1) {
          if (eachInitial) removedUnitIds.push(eachInitial);
        }
      });
      formData?.businessUnitIds.map((each: any) => {
        const unMatchedIndex = initialBunit.findIndex(
          (eachInitial: any) => each === eachInitial
        );
        if (unMatchedIndex === -1) {
          if (each) addedUnitIds.push(each);
        }
      });
      resData["removedUnitIds"] = removedUnitIds;
      resData["addedUnitIds"] = addedUnitIds;
    }
    delete resData["phone"];
    resData.defaultUnit = formData?.businessUnitIds[0];
    if (isEdit) {
      updateEmployeeService(resData)
        .then((res) => {
          toastMessage("Employee Updated Succuessfully", "success");
          console.log("Update Success");
          navigate("/organisation/employee");
        })
        .catch((e) => {
          e?.response?.data?.message &&
            toastMessage(e?.response?.data?.message, "error");
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      resData.email = resData.email.toLocaleLowerCase();
      addEmployeeService(resData)
        .then((res) => {
          toastMessage("Employee Created Succuessfully", "success");
          console.log("Registered Success");
          navigate("/organisation/employee");
        })
        .catch((e) => {
          e?.response?.data?.message &&
            toastMessage(e?.response?.data?.message, "error");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };
  const onChangeAddress = (e: any) => {
    const newForm = { ...formData };
    newForm.address.addressLine1 = e.target.value.addressLine;
    newForm.address.stateCode = e.target.value.stateCode;
    newForm.address.countryCode = e.target.value.countryCode;
    newForm.address.city = e.target.value.city;
    newForm.address.cityCode = e.target.value.cityCode || "";
    newForm.address.state = e.target.value.state;
    newForm.address.country = e.target.value.country;
    setFormData(newForm);
  };
  const isValidForm = () => {
    return (
      formData?.name &&
      formData?.email &&
      !emailError &&
      formData?.roleName &&
      formData?.department &&
      formData?.employeeCode &&
      (!formData?.mobile?.length || !mobileError) &&
      formData?.businessUnitIds?.length
    );
  };
  const validateFields = (name: string, value: string) => {
    let substrings = ["gmail", "yahoo"];
    if (name === "name") {
      setErrorData({ ...errorData, unitNameError: "" });
      if (value.length < 3 || value.length > 50) {
        setErrorData({
          ...errorData,
          unitNameError: errorMessage().BUSINESS_UNIT_NAME_ERROR,
        });
      }
    } else if (name === "email") {
      setErrorData({ ...errorData, emailError: "" });
      //regex mail
      if (!EMAIL_REGEX.test(value)) {
        //setErrorData((prevState)=>{return {...prevState,emailError: "Invalid Email"}});
        setErrorData({ ...errorData, emailError: "* Invalid Email" });
      }
    } else if (name === "registrationId") {
      setErrorData({ ...errorData, registrationIdError: "" });
      //regex mail
      if (
        value.length < 3 ||
        value.length > 50 ||
        !value.match("^[a-zA-Z0-9]*$")
      ) {
        setErrorData({
          ...errorData,
          registrationIdError: errorMessage().REGISTRATION_FORMAT,
        });
      }
    } else {
      setErrorData({ ...errorData, mobileError: "" });
      let phone: any = value.split(" ");
      let dialcode = phone[0];
      if (
        dialcode === DIAL_CODE.INDIA &&
        !PHONE_NUMBER_INDIA_REGEX.test(value)
      ) {
        setErrorData({ ...errorData, mobileError: errorMessage().PHONE_ERROR });
      } else if (
        dialcode === DIAL_CODE.SINGAPORE &&
        !PHONE_NUMBER_SINGAPORE_REGEX.test(value)
      ) {
        setErrorData({ ...errorData, mobileError: errorMessage().PHONE_ERROR });
      } else {
        if (value.length < 10 || value.length > 15)
          setErrorData({
            ...errorData,
            mobileError: "Enter valid phone number ",
          });
      }
    }
  };
  const handleChange = (event: any) => {
    if (
      event.target.files.length > 0 &&
      fileTypeMatch("image/*", event.target.files[0].type, "Invalid file type")
    ) {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      bulkUploadService(formData).then((res) => {
        setIsFileUploaded(true);
        setProfileImageUrl(res.data.Location);
        setFormData((prevState: any) => {
          return {
            ...prevState,
            profileImageUrl: res.data.Location,
          };
        });
      });
    }
  };

  const handleClick = () => {
    setIsFileUploaded(false);
    if (inputRef.current !== null) {
      inputRef.current.click();
    }
  };

  const createDepartmentList = (newDepartment: any) => {
    let company =
      localStorage.getItem("company") &&
      JSON.parse(localStorage.getItem("company") || "").id;
    const reqData = {
      company,
      name: newDepartment,
    };
    createDepartmentService(reqData)
      .then((res: any) => {
        console.log(res);
      })
      .catch((e: any) => {
        console.log(e);
      });
  };
  const getDepartmentsList = () => {
    getDepartmentsService()
      .then((res) => {
        const resData = res.data.map((each: any) => {
          return {
            value: each.name,
            label: each.name,
          };
        });
        setDepartmentOptions(resData);
      })
      .catch((e: any) => {
        console.log(e);
      });
  };
  useEffect(() => {
    if (state?.userId) {
      fetchUser(state.userId)
        .then((res) => {
          const units = res.data.users.units.map((each: any) => each.id);
          setInitialBunit(units);
          const data = res.data.users;
          const userFormData = {
            name: data.display_name,
            email: data.email,
            address: {
              addressLine1: data.address_line1,
              addressLine2: data.address_type,
              city: data.city,
              cityCode: data.city_code,
              state: data.state,
              stateCode: data.state_code,
              country: data.country,
              countryCode: data.country_code,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            roleName: data.role_name,
            department: {
              value: data.department,
              label: data.department,
            },
            employeeCode: data.employee_code,
            profileImageUrl: data.profile_image,
            designation: data.designation,
            phone: data.mobile,
            dialCode: data.dial_code,
            userId: state.userId,
          };

          setFormData({ ...userFormData });
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, []);
  useEffect(() => {
    getDepartmentsList();
  }, []);
  useEffect(() => {
    if (companyPreferences?.account_type === "TRAINER") {
      setSystemRoleTypes(trainerEmployeeRoles);
      onChange("Student", "roleName");
    }
  }, [companyPreferences]);
  return (
    <Fragment>
      <div className="pw-bunit-main w-55v overflow-y-auto">
        <div className="pw-input-label pw-create-label">
          {isEdit ? "Edit" : "Add"} Employee
        </div>
        <div className="pw-bunit-create-desc pw-border-bottom pb-2">
          Please provide necessary information below for this Employee .
        </div>
        <div className="pw-bunit-main-details flex flex-row">
          <div className="pw-left-div pw-width-50  flex flex-col w-68">
            <div className="">
              <div className="flex flex-row pw-form-field">
                <div className="pw-input-label pw-text">Name</div>
                <div className="pw-mandatory-icon">*</div>
              </div>
              <TextField
                type="text"
                variant="outlined"
                placeholder="Name"
                size="small"
                name="name"
                value={formData?.name}
                onChange={(e) => onChange(e, "name")}
                className="pw-width-100 pw-input-field pw-input-text"
                sx={styles}
              />
              {unitNameError ? (
                <div
                  className="pw-display-error"
                  id="pw-e2e-employeename-error"
                >
                  * {unitNameError}
                </div>
              ) : (
                ""
              )}
            </div>
            <div className="">
              <div className=" pw-big-field pw-email">
                <div className="pw-input-label pw-text">
                  Email <span className="pw-mandatory-icon">*</span>{" "}
                </div>
                <TextField
                  type="email"
                  disabled={isEdit}
                  variant="outlined"
                  placeholder="Email"
                  size="small"
                  value={formData?.email}
                  name="email"
                  onChange={(e) => onChange(e, "email")}
                  className="pw-width-100 pw-input-field pw-input-text"
                  sx={styles}
                />
                {emailError ? (
                  <span
                    className="pw-display-error"
                    id="pw-e2e-employee-email-error"
                  >
                    {emailError}
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>

            <div className="">
              <div className="flex flex-row pw-form-field">
                <div className="pw-input-label pw-text">System role</div>
                <div className="pw-mandatory-icon">*</div>
              </div>
              {formData?.roleName === "Admin" ? (
                <TextField
                  disabled={true}
                  variant="outlined"
                  placeholder="Email"
                  size="small"
                  value="Super User"
                  className="pw-width-100 pw-input-field pw-input-text"
                  sx={styles}
                />
              ) : (
                <Box>
                  <FormControl
                    fullWidth
                    size="small"
                    className="pw-form-select"
                  >
                    <AntSelect
                      placeholder="Select Role"
                      suffixIcon={
                        <CaretDownOutlined className="!pointer-events-none" />
                      }
                      value={formData?.roleName}
                      className="pw-creation-select pw-facility-select"
                      id="pw-e2e-role"
                      onChange={(e) => onChange(e, "roleName")}
                    >
                      {systemRoleTypes?.map((name: any, index: number) => (
                        <Option
                          value={name}
                          className="pw-menu-item"
                          id={name?.replace(/[ .]/g, "")}
                        >
                          {name}
                        </Option>
                      ))}
                    </AntSelect>
                  </FormControl>
                </Box>
              )}
            </div>
            <div className="">
              <div className="flex flex-row pw-form-field">
                <div className="pw-input-label pw-text">Employee Code</div>
                <div className="pw-mandatory-icon">*</div>
              </div>
              <TextField
                type="text"
                disabled={
                  isEdit && !checkPermissions(["CREATE_EMPLOYEE"], permissions)
                }
                variant="outlined"
                placeholder="Employee code"
                size="small"
                name="employeeCode"
                value={formData?.employeeCode}
                onChange={(e) => onChange(e, "employeeCode")}
                className="pw-width-100 pw-input-field pw-input-text"
                sx={styles}
              />
              {unitNameError ? (
                <div
                  className="pw-display-error"
                  id="pw-e2e-employee-code-error"
                >
                  * {unitNameError}
                </div>
              ) : (
                ""
              )}
            </div>
            <div style={{ display: "flex", columnGap: "30px", width: "59rem" }}>
              <div style={{ width: "50%" }} className="">
                <div className="flex flex-row pw-form-field">
                  <div className="pw-input-label pw-text">Designation</div>
                </div>
                <TextField
                  type="designation"
                  variant="outlined"
                  placeholder="Designation"
                  size="small"
                  value={formData?.designation ? formData.designation : ""}
                  name="designation"
                  onChange={(e) => onChange(e, "designation")}
                  className="pw-width-100 pw-input-field pw-input-text"
                  sx={styles}
                />
              </div>
              <div style={{ width: "50%" }} className="">
                <div className="flex flex-row pw-form-field">
                  <div className="pw-input-label pw-text">Department</div>
                  <div className="pw-mandatory-icon">*</div>
                </div>

                <CreatableSelect
                  value={formData?.department}
                  onChange={handleDeptChangeSelect}
                  onCreateOption={handleDeptCreateOption}
                  onMenuOpen={clearDeptInput}
                  options={departmentOptions}
                  id="pw-e2e-departmentselection"
                  placeholder="Search department or create..."
                />

                {/* <Box>
                  <FormControl
                    fullWidth
                    size="small"
                    className="pw-form-select"
                  >
                    <AntSelect
                      suffixIcon={
                        <CaretDownOutlined className="!pointer-events-none" />
                      }
                      placeholder="Select department"
                      value={formData?.department}
                      className="pw-creation-select pw-facility-select"
                      onChange={(e) => onChange(e, "department")}
                    >
                      {departments?.map((name: any, index: number) => (
                        <Option
                          value={name}
                          className="pw-menu-item"
                          id={name?.replace(/[ .]/g, "")}
                        >
                          {name}
                        </Option>
                      ))}
                    </AntSelect>
                  </FormControl>
                </Box> */}
              </div>
            </div>
            {isEdit ? (
              initialBunit?.length && (
                <div
                  className="pw-linked-businessunit"
                  style={{ width: "100%", height: "100%" }}
                >
                  <div className="flex flex-row pw-form-field">
                    <div className="pw-input-label pw-text">
                      Link to facility
                    </div>
                    <div className="pw-mandatory-icon">*</div>
                  </div>
                  <Space
                    className="space-container"
                    style={{ width: "100%", height: "100%" }}
                    direction="vertical"
                  >
                    <AntSelect
                      mode="multiple"
                      allowClear
                      disabled={formData?.roleName === "Admin"}
                      showSearch={false}
                      defaultValue={
                        initialBunit?.length > 0
                          ? initialBunit
                          : bunitsList?.length === 1
                          ? bunitsList[0].label
                          : []
                      }
                      style={{ width: "100%", height: "100%" }}
                      placeholder="Select Facilities"
                      id="pw-e2e-facilitydropdown"
                      onChange={(e) => onChange(e, "businessUnitIds")}
                      options={bunitsList?.length && bunitsList}
                    />
                  </Space>
                </div>
              )
            ) : (
              <div
                className="pw-linked-businessunit"
                style={{ width: "100%", height: "100%" }}
              >
                <div className="flex flex-row pw-form-field">
                  <div className="pw-input-label pw-text">Link to facility</div>
                  <div className="pw-mandatory-icon">*</div>
                </div>
                <Space
                  className="space-container"
                  style={{ width: "100%", height: "100%" }}
                  direction="vertical"
                >
                  <AntSelect
                    mode="multiple"
                    allowClear
                    disabled={formData?.roleName === "Admin"}
                    showSearch={false}
                    defaultValue={
                      initialBunit?.length > 0
                        ? initialBunit
                        : bunitsList?.length === 1
                        ? bunitsList[0].label
                        : []
                    }
                    style={{ width: "100%", height: "100%" }}
                    placeholder="Select Facilities"
                    id="pw-e2e-facilitydropdown"
                    onChange={(e) => onChange(e, "businessUnitIds")}
                    options={bunitsList?.length && bunitsList}
                  />
                </Space>
              </div>
            )}
          </div>
          <div className="pw-right-div flex  flex-col justify-start mt-10 mx-auto">
            <input
              type="file"
              accept="image/*"
              id="pw-e2e-employee-image"
              hidden
              ref={inputRef}
              onChange={handleChange}
            />

            <div>
              <img
                src={
                  formData?.profileImageUrl
                    ? formData.profileImageUrl
                    : "assets/images/camera.png"
                }
                alt=" "
                onClick={handleClick}
                className="pw-circ-image h-35v"
              />
            </div>
          </div>
        </div>
        <div className="pw-multi-fields w-full flex flex-row justify-between">
          <div className=" pw-small-field pw-width-48">
            <div className="flex flex-row pw-form-field">
              <div className="pw-input-label pw-text">Contact No.</div>
            </div>
            <PhoneInput
              initialCountry="in"
              value={phone}
              onChange={(e) => onChangePhone(e)}
              className="pw-bunit-phone-input pw-phonecode-select"
            />
            {mobileError ? (
              <span className="pw-display-error" id="pw-e2e-mobile-error">
                {mobileError}
              </span>
            ) : (
              ""
            )}
          </div>
        </div>
        <div className="">
          <div className="flex flex-row pw-form-field">
            <div className="pw-input-label pw-text">Address</div>
            {/* <div className="pw-mandatory-icon">*</div> */}
          </div>

          <MapsInput
            isTextArea
            defaultValue={formData?.address}
            placeholder="Address"
            value={formData?.address?.addressLine1 || ""}
            onChange={onChangeAddress}
            formData={formData}
            setFormData={setFormData}
            className="pw-width-100 pw-input-field pw-input-text"
            sx={styles}
          />
          {unitNameError ? (
            <div className="pw-display-error" id="pw-e2e-address-error">
              * {unitNameError}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      <div className="flex flex-row justify-end pw-bunit-footer w-full gap-6">
        <div
          className="pw-bunit-button-light pw-bunit-button-next text-neutral bg-white font-bold"
          onClick={handleCancel}
        >
          Cancel
        </div>
        <button
          id="pw-e2e-employeesave-button"
          className={`pw-bunit-button-primary pw-bunit-button-next text-white  font-bold ${
            isValidForm()
              ? isLoading
                ? "text-black bg-neutral-300"
                : "cursor-pointer bg-primary"
              : "text-black bg-neutral-300"
          }`}
          onClick={() => {
            isValidForm() && createEmployee();
          }}
          disabled={!isValidForm() || isLoading}
        >
          {isLoading ? (
            <div className="flex flex-row gap-2 p-2">
              <span>{"Saving up"}</span>
              <CircularProgress size={20} />
            </div>
          ) : (
            <span>{"Save"}</span>
          )}
        </button>
      </div>
    </Fragment>
  );
};

export default EmployeeCreation;
